import * as React from 'react'
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  SelectProps,
  TextField,
  Box,
  IconButton
} from '@mui/material'
import { Search, Clear } from '@mui/icons-material'

/**
 * SelectWithCheckbox Component
 *
 * A multi-select dropdown with checkboxes, search, and optional "Select All" and "None" features.
 *
 * @example
 * ```tsx
 * <SelectWithCheckbox
 *   label="Select Items"
 *   items={[
 *     { label: 'Option 1', value: 'opt1' },
 *     { label: 'Option 2', value: 'opt2' }
 *   ]}
 *   value={selectedValues}
 *   onChange={(event, values) => setSelectedValues(values)}
 *   showSelectAll
 *   noneOption={{ label: 'None', value: 'none' }}
 * />
 * ```
 */

type SelectItem = string | { label: string; value: string }

/**
 * Props for SelectWithCheckbox component.
 *
 * Note: This component uses a custom onChange signature where the second argument
 * is the selected values array (string[]), not the MUI child node.
 */
type SelectWithCheckboxProps = Omit<
  SelectProps<string[]>,
  'onChange' | 'renderValue' | 'multiple'
> & {
  /** Array of items to display in the select */
  items: SelectItem[]
  /** Tooltip text to show when the select is disabled */
  disabledTooltip?: string
  /** Show "Select All" option */
  showSelectAll?: boolean
  /** Option for "none" selection that disables all other options when selected */
  noneOption?: { label: string; value: string }
  /**
   * Callback fired when the selection changes.
   * @param event - The event source of the callback
   * @param value - The new selected values as a string array
   */
  onChange?: (event: any, value: string[]) => void
}

/**
 * A multi-select component with checkboxes, search functionality, and optional "Select All" and "None" options.
 *
 * Key features:
 * - Search/filter items
 * - Select all filtered items
 * - Optional "none" option that disables all other selections
 * - Custom onChange signature: (event, value: string[]) - NOT the standard MUI Select onChange
 */
const SelectWithCheckbox: React.FC<SelectWithCheckboxProps> = props => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [open, setOpen] = React.useState(false)

  const selectedNames = props.value || []

  // Helper to get value from item
  const getItemValue = (item: SelectItem): string => {
    return typeof item === 'string' ? item : item.value
  }

  // Helper to get label from item
  const getItemLabel = (item: SelectItem): string => {
    return typeof item === 'string' ? item : item.label
  }

  /**
   * Clean selection when items change to prevent invalid selections
   * But preserve valid selections that still exist in the new items list
   */
  React.useEffect(() => {
    if (props.onChange && selectedNames.length > 0 && props.items.length > 0) {
      // Get all valid values from current items
      const validValues = new Set(props.items.map(getItemValue))

      // Also include noneOption value if it exists
      if (props.noneOption) {
        validValues.add(props.noneOption.value)
      }

      // Filter out any invalid selections
      const validSelections = selectedNames.filter(value =>
        validValues.has(value)
      )

      // Only clear if there are invalid selections AND no valid ones remain
      if (validSelections.length === 0 && selectedNames.length > 0) {
        const mockEvent = {
          target: { value: [] }
        } as any
        props.onChange(mockEvent, [])
      }
    }
  }, [props.items, props.noneOption, props.onChange])

  /**
   * Handles selection change with special logic for "none" option.
   * When "none" is selected, all other options are cleared.
   * When any other option is selected while "none" is active, "none" is removed.
   *
   * Note: Creates mock events with consistent target.value for filtered results
   * to ensure consumers reading event.target.value get the correct data.
   */
  const handleChange = (event: any) => {
    const { value } = event.target
    const newValue = typeof value === 'string' ? value.split(',') : value

    // Handle "none" option logic
    if (props.noneOption) {
      const noneValue = props.noneOption.value
      const hasNoneSelected = newValue.includes(noneValue)
      const previouslyHadNone = selectedNames.includes(noneValue)

      // If "none" was just selected - clear all other selections
      if (hasNoneSelected && !previouslyHadNone) {
        const finalValue = [noneValue]
        const mockEvent = {
          target: { value: finalValue }
        } as any
        if (props.onChange) {
          props.onChange(mockEvent, finalValue)
        }
        return
      }

      // If other options were selected while "none" was already selected - remove "none"
      if (hasNoneSelected && previouslyHadNone && newValue.length > 1) {
        const finalValue = newValue.filter((v: string) => v !== noneValue)
        const mockEvent = {
          target: { value: finalValue }
        } as any
        if (props.onChange) {
          props.onChange(mockEvent, finalValue)
        }
        return
      }
    }

    if (props.onChange) {
      props.onChange(event, newValue)
    }
  }

  /**
   * Toggles selection of all filtered items.
   * If all filtered items are selected, deselects them.
   * Otherwise, selects all filtered items (preserving existing selections).
   */
  const handleSelectAll = () => {
    const filteredValues = filteredItems.map(getItemValue)
    const allSelected =
      filteredValues.length > 0 &&
      filteredValues.every(value => selectedNames.includes(value))

    let newValue: string[]
    if (allSelected) {
      // Remove all filtered items from selection
      newValue = selectedNames.filter(item => !filteredValues.includes(item))
    } else {
      // Add all filtered items to selection (avoiding duplicates)
      const selectedSet = new Set(selectedNames)
      filteredValues.forEach(value => selectedSet.add(value))
      newValue = Array.from(selectedSet)
    }

    const mockEvent = {
      target: { value: newValue }
    } as any
    if (props.onChange) {
      props.onChange(mockEvent, newValue)
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const clearSearch = () => {
    setSearchTerm('')
  }

  // Filter items based on search term
  const filteredItems = (props.items || []).filter(item => {
    const label = getItemLabel(item)
    return label.toLowerCase().includes(searchTerm.toLowerCase())
  })

  // Combine filtered items with already selected items (to not lose selections)
  const itemsToShow = React.useMemo(() => {
    const filteredValues = new Set(filteredItems.map(getItemValue))
    const selectedItems = props.items.filter(item => {
      const value = getItemValue(item)
      return selectedNames.includes(value) && !filteredValues.has(value)
    })
    return [...selectedItems, ...filteredItems]
  }, [filteredItems, selectedNames, props.items])

  const filteredValues = filteredItems.map(getItemValue)
  const allFilteredSelected =
    filteredValues.length > 0 &&
    filteredValues.every(value => selectedNames.includes(value))

  // Get labels for selected values
  const getSelectedLabels = (): string => {
    const itemMap = new Map(
      props.items.map(item => [getItemValue(item), getItemLabel(item)])
    )

    // Add none option to map if provided
    if (props.noneOption) {
      itemMap.set(props.noneOption.value, props.noneOption.label)
    }

    return selectedNames.map(value => itemMap.get(value) || value).join(', ')
  }

  // Check if "none" option is selected
  const isNoneSelected =
    props.noneOption && selectedNames.includes(props.noneOption.value)

  return (
    <FormControl fullWidth>
      <InputLabel id='checkbox-select-label'>{props.label}</InputLabel>
      <Select
        labelId='checkbox-select-label'
        label={props.label}
        multiple
        value={selectedNames}
        onChange={handleChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        renderValue={() => getSelectedLabels()}
        disabled={props.disabled}
        sx={{
          backgroundColor: 'background.paper',
          '& .Mui-disabled': { cursor: 'not-allowed' }
        }}
        title={props.disabled ? props.disabledTooltip : ''}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300
            }
          }
        }}
      >
        <MenuItem disableRipple style={{ backgroundColor: 'transparent' }}>
          <Box sx={{ width: '100%', px: 1, py: 0.5 }}>
            <TextField
              size='small'
              placeholder='Pesquisar...'
              value={searchTerm}
              onChange={handleSearchChange}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              onKeyUp={e => e.stopPropagation()}
              slotProps={{
                input: {
                  startAdornment: (
                    <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                  endAdornment: searchTerm && (
                    <IconButton
                      size='small'
                      onClick={e => {
                        e.stopPropagation()
                        clearSearch()
                      }}
                    >
                      <Clear fontSize='small' />
                    </IconButton>
                  )
                }
              }}
              sx={{ width: '100%' }}
            />
          </Box>
        </MenuItem>
        {props.noneOption && (
          <MenuItem
            value={props.noneOption.value}
            disableRipple
            onKeyDown={e => {
              // Handle Enter or Space to toggle the "none" option
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                const finalValue = isNoneSelected
                  ? []
                  : [props.noneOption!.value]
                const mockEvent = {
                  target: { value: finalValue }
                } as any
                if (props.onChange) {
                  props.onChange(mockEvent, finalValue)
                }
              } else {
                e.stopPropagation()
              }
            }}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: isNoneSelected
                ? 'action.selected'
                : 'background.paper',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Checkbox
              checked={isNoneSelected}
              onClick={e => {
                e.stopPropagation()
                const finalValue = isNoneSelected
                  ? []
                  : [props.noneOption!.value]
                const mockEvent = {
                  target: { value: finalValue }
                } as any
                if (props.onChange) {
                  props.onChange(mockEvent, finalValue)
                }
              }}
            />
            <ListItemText
              primary={props.noneOption.label}
              sx={{ fontWeight: 'medium' }}
              onClick={e => {
                e.stopPropagation()
                const finalValue = isNoneSelected
                  ? []
                  : [props.noneOption!.value]
                const mockEvent = {
                  target: { value: finalValue }
                } as any
                if (props.onChange) {
                  props.onChange(mockEvent, finalValue)
                }
              }}
            />
          </MenuItem>
        )}
        {props.showSelectAll && filteredItems.length > 0 && (
          <MenuItem
            disableRipple
            onKeyDown={e => e.stopPropagation()}
            disabled={isNoneSelected}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'action.hover',
              '&:hover': {
                backgroundColor: 'action.selected'
              }
            }}
          >
            <Checkbox
              checked={allFilteredSelected}
              indeterminate={selectedNames.length > 0 && !allFilteredSelected}
              disabled={isNoneSelected}
              onClick={e => {
                e.stopPropagation()
                handleSelectAll()
              }}
            />
            <ListItemText
              primary='Selecionar todos'
              sx={{ fontWeight: 'medium' }}
              onClick={e => {
                e.stopPropagation()
                if (!isNoneSelected) {
                  handleSelectAll()
                }
              }}
            />
          </MenuItem>
        )}
        {itemsToShow.map(item => {
          const value = getItemValue(item)
          const label = getItemLabel(item)
          const isFiltered = filteredItems.some(
            fItem => getItemValue(fItem) === value
          )
          return (
            <MenuItem key={value} value={value} disabled={isNoneSelected}>
              <Checkbox
                checked={selectedNames.includes(value)}
                disabled={isNoneSelected}
              />
              <ListItemText
                primary={label}
                sx={{
                  opacity: isFiltered ? 1 : 0.6
                }}
              />
            </MenuItem>
          )
        })}
        {filteredItems.length === 0 && searchTerm && (
          <MenuItem disabled>
            <ListItemText primary='Nenhum item encontrado' />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  )
}

export default SelectWithCheckbox
